import { isValid, parseISO } from 'date-fns';

/**
 * Inputs accepted by the lenient parsers. These mirror what call sites used to
 * hand to `moment(...)` / `dayjs(...)`: API strings, epoch milliseconds, `Date`
 * objects, and `null`/`undefined` from optional fields.
 */
export type DateInput = Date | string | number | null | undefined;

const invalidDate = () => new Date(NaN);

// moment's ISO 8601 shapes. A string with one of these shapes is never handed
// to the native parser, so out-of-range values such as `2024-02-30` stay
// invalid instead of rolling over.
const ISO_EXTENDED =
  /^\s*((?:[+-]\d{6}|\d{4})-(?:\d\d-\d\d|W\d\d-\d|W\d\d|\d\d\d|\d\d))(?:(T| )(\d\d(?::\d\d(?::\d\d(?:[.,]\d+)?)?)?)([+-]\d\d(?::?\d\d)?|\s*Z)?)?$/;
const ISO_BASIC =
  /^\s*((?:[+-]\d{6}|\d{4})(?:\d\d\d\d|W\d\d\d|W\d\d|\d\d\d|\d\d|))(?:(T| )(\d\d(?:\d\d(?:\d\d(?:[.,]\d+)?)?)?)([+-]\d\d(?::?\d\d)?|\s*Z)?)?$/;

// An ISO 8601 date-time that carries its own UTC designator or numeric offset.
const ISO_ZONE_DESIGNATOR = /[T ]\d.*(?:Z|[+-]\d\d(?::?\d\d)?)$/i;

const parseIsoLike = (value: string) => parseISO(value.replace(/^\s+/, ''));
const hasIsoShape = (value: string) =>
  ISO_EXTENDED.test(value) || ISO_BASIC.test(value);

/**
 * Lenient local-time parser with moment's `moment(value)` semantics:
 * - `undefined` is "now"; `null` and `''` are invalid;
 * - `Date` is cloned and numbers are epoch milliseconds;
 * - strings are tried as ISO 8601 (date-only and offset-less values are local
 *   time), then fall back to the native `Date` parser.
 *
 * Invalid input yields an invalid `Date` (check with date-fns `isValid`).
 */
export function toDate(value?: DateInput): Date {
  if (value === undefined) return new Date();
  if (value === null) return invalidDate();
  if (value instanceof Date) return new Date(value.getTime());
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    if (value === '') return invalidDate();
    const iso = parseIsoLike(value);
    if (isValid(iso) || hasIsoShape(value)) return iso;
    return new Date(value);
  }
  return invalidDate();
}

/**
 * Parser with moment's `moment.utc(value)` semantics: ISO strings without an
 * explicit zone are read as UTC instead of local time, and non-ISO strings are
 * handed to the native parser with a ` UTC` suffix.
 */
export function toUtcDate(value?: DateInput): Date {
  if (typeof value !== 'string' || value === '') return toDate(value);
  const local = parseIsoLike(value);
  if (isValid(local)) {
    const trimmed = value.replace(/^\s+/, '');
    if (ISO_ZONE_DESIGNATOR.test(trimmed)) return local;
    const asUtc = /[T ]\d/i.test(trimmed)
      ? `${trimmed}Z`
      : `${trimmed}T00:00:00Z`;
    const utc = parseISO(asUtc);
    if (isValid(utc)) return utc;
  }
  if (hasIsoShape(value)) return local;
  return new Date(`${value} UTC`);
}

/**
 * Parser with the `moment.tz(value, zone)` semantics (timezone plugin) when
 * `zone` is the browser's own zone (the only way the legacy app called it):
 * - ISO 8601 strings behave like `toDate` (offset-less values are local wall
 *   time; `Z`/offset values are that instant);
 * - any other string is read as UTC (`new Date(value + ' UTC')`), because
 *   moment.tz builds the value with `moment.utc` before converting to the zone.
 *   For example `'08/08/24 12:18 am'` is 00:18 UTC, i.e. 08/07/24 05:18 pm in
 *   America/Los_Angeles;
 * - `Date`/number input is the same instant; `undefined` is now; `null` and
 *   `''` are invalid.
 */
export function toDateFromZone(value?: DateInput): Date {
  if (typeof value !== 'string' || value === '' || hasIsoShape(value)) {
    return toDate(value);
  }
  return new Date(`${value} UTC`);
}

// dayjs' own string pattern; anything else goes to the native parser.
const DAYJS_REGEX_PARSE =
  /^(\d{4})[-/]?(\d{1,2})?[-/]?(\d{0,2})[Tt\s]*(\d{1,2})?:?(\d{1,2})?:?(\d{1,2})?[.:]?(\d+)?$/;

/**
 * Parser with `dayjs(value)` semantics, kept for the call sites that used
 * dayjs so their (slightly different) string handling is unchanged.
 */
export function toDateDayjs(value?: DateInput): Date {
  if (value === null) return invalidDate();
  if (value === undefined) return new Date();
  if (value instanceof Date) return new Date(value.getTime());
  if (typeof value === 'string' && !/Z$/i.test(value)) {
    const d = value.match(DAYJS_REGEX_PARSE);
    if (d) {
      const month = Number(d[2]) - 1 || 0;
      const ms = Number((d[7] || '0').substring(0, 3));
      return new Date(
        Number(d[1]),
        month,
        Number(d[3] || 1),
        Number(d[4] || 0),
        Number(d[5] || 0),
        Number(d[6] || 0),
        ms
      );
    }
  }
  return new Date(value as string | number);
}

const FORMAT_TOKENS = /YYYY|MM|DD|./g;
const TOKEN_REGEX: Record<string, RegExp> = {
  YYYY: /\d{1,4}/,
  MM: /\d\d?/,
  DD: /\d\d?/,
};

const escapeRegex = (s: string) => s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

/**
 * Forgiving format parser with the semantics of moment's non-strict
 * `moment(value, format)` for formats built from `YYYY`, `MM`, `DD` and
 * literal separators (for example `MM/DD/YYYY` or `YYYY-MM-DD`):
 * - a `Date` ignores the format and is cloned; `null` is invalid;
 * - each token takes the first matching digits anywhere in the remaining
 *   text, so trailing time parts (`2024-01-31T00:00:00Z`) are ignored;
 * - missing leading parts default to today's date, missing trailing parts to
 *   January / the 1st; out-of-range months or days produce an invalid date;
 * - the result is local midnight.
 */
export function parseWithFormat(value: DateInput, formatString: string): Date {
  if (value === null) return invalidDate();
  if (value instanceof Date) return new Date(value.getTime());

  let input = `${value}`;
  const parts: Array<number | undefined> = [undefined, undefined, undefined];
  let empty = true;

  (formatString.match(FORMAT_TOKENS) || []).forEach((token) => {
    const regex = TOKEN_REGEX[token] || new RegExp(escapeRegex(token));
    const match = input.match(regex);
    if (match && match.index !== undefined) {
      input = input.slice(match.index + match[0].length);
    }
    if (!TOKEN_REGEX[token] || !match) return;
    empty = false;
    const parsed = match[0];
    if (token === 'YYYY') {
      const year = parseInt(parsed, 10);
      parts[0] = parsed.length === 2 ? year + (year > 68 ? 1900 : 2000) : year;
    } else if (token === 'MM') {
      parts[1] = parseInt(parsed, 10) - 1;
    } else {
      parts[2] = parseInt(parsed, 10);
    }
  });

  if (empty) return invalidDate();

  const today = new Date();
  const current = [today.getFullYear(), today.getMonth(), today.getDate()];
  for (let i = 0; i < 3 && parts[i] === undefined; i += 1) {
    parts[i] = current[i];
  }
  const year = parts[0] as number;
  const month = parts[1] ?? 0;
  const day = parts[2] ?? 1;

  if (month < 0 || month > 11) return invalidDate();
  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const daysInMonth =
    month === 1 ? 28 + (isLeapYear ? 1 : 0) : 31 - ((month % 7) % 2);
  if (day < 1 || day > daysInMonth) return invalidDate();

  const date = new Date(year, month, day);
  if (year < 100 && year >= 0) date.setFullYear(year);
  return date;
}
